import { buildCsViewCoursePath } from "@/modules/Course-Management/utils/csViewContext";
import { resolveStudentType } from "@/modules/User-Management/components/StudentTypeBadge";
import type { StudentModel } from "@/types/student.model";

export type StudentResourceRecord = StudentModel & {
  class_offering?: { id: string; name?: string } | null;
  class?:
    | { id: string; name?: string }
    | { id: string; name?: string }[]
    | null;
};

export type StudentCourseViewState = {
  selectedTab: string;
  selectedStudentId?: string;
  viewType?: "course" | "class";
  assignmentType: null;
  lessonId: null;
  unitId: null;
};

export function resolveStudentGradeId(record: StudentModel): string | null {
  const { grade } = record;
  if (Array.isArray(grade)) {
    const first = grade[0];
    return typeof first === "object" && first !== null ? first.id : null;
  }
  if (typeof grade === "object" && grade !== null && "id" in grade) {
    return grade.id;
  }
  return null;
}

export function resolveStudentClassOfferingId(
  record: StudentResourceRecord
): string | null {
  if (record.class_offering?.id?.trim()) {
    return record.class_offering.id.trim();
  }

  const cls = record.class;
  if (Array.isArray(cls)) {
    const id = cls[0]?.id?.trim();
    return id || null;
  }
  if (cls && typeof cls === "object" && "id" in cls) {
    const id = cls.id?.trim();
    return id || null;
  }

  if (resolveStudentType(record)?.toLowerCase() === "class") {
    return resolveStudentGradeId(record);
  }

  return null;
}

/** Class offering display name for `type: "class"` students; empty for course/other. */
export function resolveStudentClassName(
  record: StudentResourceRecord
): string {
  if (resolveStudentType(record)?.toLowerCase() !== "class") {
    return "";
  }

  const fromOffering = record.class_offering?.name?.trim();
  if (fromOffering) return fromOffering;

  const cls = record.class;
  if (Array.isArray(cls)) {
    return cls[0]?.name?.trim() || "";
  }
  if (cls && typeof cls === "object" && "name" in cls) {
    return cls.name?.trim() || "";
  }

  return "";
}

export function getStudentCourseViewNavigation(
  record: StudentResourceRecord
): { path: string; state: StudentCourseViewState } | null {
  const studentUserId = record.user_id;
  const type = resolveStudentType(record)?.toLowerCase();

  if (type === "class") {
    const classId = resolveStudentClassOfferingId(record);
    if (!classId) return null;

    return {
      path: buildCsViewCoursePath(classId, "class"),
      state: {
        selectedTab: "unit",
        selectedStudentId: studentUserId,
        viewType: "class",
        assignmentType: null,
        lessonId: null,
        unitId: null,
      },
    };
  }

  const gradeId = resolveStudentGradeId(record);
  if (!gradeId) return null;

  return {
    path: `/course-management/view-course/${gradeId}`,
    state: {
      selectedTab: "unit",
      selectedStudentId: studentUserId,
      assignmentType: null,
      lessonId: null,
      unitId: null,
    },
  };
}
